import { css } from "@emotion/react";
import { designTokens } from "@itandi/itandi-bb-ui";
import { Alert } from "@itandi/itandi-bb-ui/Alert";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { Paragraph } from "@itandi/itandi-bb-ui/Paragraph";
import { SingleComboBox } from "@itandi/itandi-bb-ui/SingleComboBox";
import type { ComponentProps } from "react";
import { useMemo, useState } from "react";

export type PartnerVendorSelectOption = {
	address?: string | null;
	id: number;
	isSelf: boolean;
	name: string;
};

type Props = {
	currentSelectedId: number | null;
	onAccept: (vendorCompanyId: number) => Promise<void>;
	onClose: () => void;
	partnerVendors: PartnerVendorSelectOption[];
	selfVendor: PartnerVendorSelectOption;
};

type VendorComboBoxOption = {
	label: string;
	value: string;
};

export function PartnerVendorSelectModal({
	currentSelectedId,
	onAccept,
	onClose,
	partnerVendors,
	selfVendor
}: Props) {
	const [selectedId, setSelectedId] = useState(currentSelectedId);
	const vendors = useMemo(
		() => [selfVendor, ...partnerVendors],
		[partnerVendors, selfVendor]
	);

	const vendorOptions = useMemo<VendorComboBoxOption[]>(() => {
		return vendors.map((vendor) => ({
			label: vendor.isSelf
				? `${vendor.name}（実施会社に依頼しない）`
				: vendor.name,
			value: String(vendor.id)
		}));
	}, [vendors]);

	const selectedVendor = useMemo(() => {
		if (selectedId === null) return;

		return vendors.find((vendor) => vendor.id === selectedId);
	}, [selectedId, vendors]);

	const selectedVendorOption = useMemo(() => {
		if (selectedId === null) return;

		return vendorOptions.find((option) => option.value === String(selectedId));
	}, [selectedId, vendorOptions]);

	const selectedVendorAddress = selectedVendor?.address;

	const handleClose = (): void => {
		onClose();
	};

	const onVendorChange: Required<
		ComponentProps<typeof SingleComboBox>
	>["onChange"] = (option): void => {
		if (option) {
			setSelectedId(Number(option.value));
		} else {
			setSelectedId(null);
		}
	};

	const handleAccept = async (): Promise<void> => {
		if (selectedId !== null) {
			await onAccept(selectedId);
		}
	};

	return (
		<Modalv2 isOpen onClose={handleClose}>
			<ModalHeader title="実施会社の選択" />
			<ModalBody>
				<Paragraph mb={designTokens["spacing/m"]}>
					依頼せず直接実施する場合は自社を選択してください。
				</Paragraph>
				<SingleComboBox
					maxMenuHeight={240}
					mb={designTokens["spacing/xs"]}
					onChange={onVendorChange}
					options={vendorOptions}
					placeholder="実施会社を選択"
					value={selectedVendorOption ?? null}
				/>
				{selectedVendorAddress ? (
					<div css={css({ marginBottom: designTokens["spacing/m"] })}>
						住所: {selectedVendorAddress}
					</div>
				) : null}
				<Alert mt={designTokens["spacing/m"]} variant="warning">
					依頼先を変更する場合は、変更理由をチャットで管理会社様へご連絡ください。
					<br />
					提携先を選択すると依頼メールが送信されます。
				</Alert>
			</ModalBody>
			<ModalFooter
				leftArea={
					<Button onClick={handleClose} type="button" variant="secondary">
						キャンセル
					</Button>
				}
				rightArea={
					<Button
						disabled={selectedId === null}
						onClick={handleAccept}
						type="button"
						variant="primary"
					>
						変更
					</Button>
				}
			/>
		</Modalv2>
	);
}
